<template>
  <div class="advisors-list" :class="advisor.cardName">
    <div class="flex flex-col flex-wrap md:flex-row justify-between md:items-center md:pb-32">
      <div class="pr-24 pb-16">
        <slot name="title" :total="total" :loading="loading"/>
      </div>
      <div v-if="hasFilters" class="flex items-center w-full md:w-auto pb-16">
        <div class="border border-neutral-30 rounded-8 h-40 md:h-48 flex items-center px-12 md:px-16 mr-12 md:mr-24">
          <div>
            <se-switch v-model="filters.onlineOnly">
              <p class="text-h-m font-semibold whitespace-nowrap">
                Online only
              </p>
            </se-switch>
          </div>
        </div>
        <se-select v-model="filters.sort" class="w-full max-w-272 !h-40 md:!h-48">
          <template #prefix>
            <se-icon name="arrow-down-up" :size="20" class="mr-12"/>
          </template>
          <template #default>
            <se-option value="rankings_desc" label="Top-Rated"/>
            <se-option value="sessions_desc" label="Most Sessions Held"/>
            <se-option value="registered_desc" label="Newest"/>
            <se-option value="price_asc" label="Price: Low to High"/>
            <se-option value="price_desc" label="Price: High to Low"/>
          </template>
        </se-select>
      </div>
    </div>
    <div v-if="hasFilters && filters.search?.length"
         class="flex items-center justify-between md:justify-start pb-24 md:pb-32 pt-8 md:pt-0 gap-16">
      <p class="text-b-xl font-medium">
        Search: {{ filters.search }}
      </p>
      <se-button size="small" type="tertiary" @click="filters.search = ''">
        Clear
      </se-button>
    </div>
    <div v-if="advisors.length > 0" class="advisors-list-container">
      <article v-for="advisor of advisors" :key="advisor.id" class="w-full">
        <se-advisor-card :advisor="advisor"/>
      </article>
    </div>
    <div v-else-if="loading" class="flex justify-center py-88">
      <se-spinner/>
    </div>
    <div v-else>
      <h6 class="text-b-xl">
        There are no advisors matching your criteria
      </h6>
    </div>
    <div v-if="hasPaging" id="loadMoreButton" class="flex justify-center items-center gap-2 flex-col mt-28 pb-10">
      <p v-if="advisors.length" class="text-b-xs text-neutral-40">
        Loaded {{ advisors.length }} of {{ total }}
      </p>
      <se-button
        :disabled="advisors.length >= total"
        :loading="loading && advisors.length > 0"
        size="large"
        class="!px-28"
        @click="loadMore()"
      >
        Load more
      </se-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {nextTick, ref, watch} from 'vue';
import {storeToRefs} from 'pinia';
import {usersStore} from '@/src/store/users';
import {getGlobalProperties} from '@/src/shared/helpers/getGlobalProperties';
import {getWindow} from '@/src/shared/helpers/getWindow';
import SeButton from '@/src/shared/ui-kit/button/Button';
import SeIcon from '@/src/shared/ui-kit/icon/Icon';
import SeSwitch from '@/src/shared/ui-kit/switch/Switch';
import SeSelect from '@/src/shared/ui-kit/select/Select';
import SeOption from '@/src/shared/ui-kit/select/Option';
import SeSpinner from '@/src/shared/ui-kit/spinner/Spinner';
import SeAdvisorCard from './advisor-card';
import {theme} from "@/theme";
import {useAdvisorListPaging} from "@/src/shared/helpers/advisorListPaging";

const {advisor} = theme.home

export interface AdvisorsFilters {
  search: string;
  categories: string[];
  onlineOnly: boolean;
  page: number;
  perPage: number;
  total: number;
  sort: string,
}

const props = withDefaults(defineProps<{
  query: Partial<AdvisorsFilters>
  hasPaging: boolean,
  hasFilters?: boolean,
}>(), {
  hasFilters: true,
});

const {$api, $route, $auth} = getGlobalProperties();

const advisors = ref<any[]>([]);
const {currentUser, isAdvisor} = storeToRefs(usersStore());

const defaultFilters: AdvisorsFilters = {
  search: '',
  categories: [],
  onlineOnly: true,
  sort: 'rankings_desc',
};

const filters = ref<AdvisorsFilters>(defaultFilters);

const loading = ref<boolean>(false);
const firstLoad = ref<boolean>(true);

const {page, perPage, total, nextPage, setTotal, resetPage} = useAdvisorListPaging();

const fetchAdvisors = async () => {
  if (loading.value) {
    return;
  }
  loading.value = true;
  
  const [sort, order] = filters.value.sort.split('_');

  const query = {
    query: filters.value.search || undefined,
    sort,
    order,
    limit: perPage.value,
    page: page.value,
    'categories[]': filters.value.categories?.length ? filters.value.categories[0] : undefined,
    status: filters.value.onlineOnly ? 'online' : undefined,
  };
  
  Object.keys(query).forEach((key) => {
    if (!query[key]) {
      delete query[key];
    }
  });
  
  await $api.get($auth?.check() ? '/api/user/advisors' : '/user/advisors', query)
    .then(({data}: any) => {
      if (page.value > 1) {
        advisors.value = [...advisors.value, ...data.data];
      } else {
        advisors.value = [...data.data];
      }
      
      setTotal(data.total);
      
      nextTick(() => scrollToNextAdvisors(data.data));
    })
    .finally(() => {
      loading.value = false;
      if ($route.query.type == 'more' && firstLoad.value) {
        firstLoad.value = false;
        loadMore();
      }
    });
};

const scrollToNextAdvisors = (data: any) => {
  if (page.value > 1) {
    const {windowWidth} = getWindow();
    if (windowWidth.value > 1224) {
      const loadMoreButton = document.getElementById('loadMoreButton');
      const headerElem = document.getElementsByTagName('header');
      const cardElem = document.getElementsByClassName('advisor-card');
      
      if (loadMoreButton) {
        const buttonPosition = loadMoreButton.getBoundingClientRect().top + window.scrollY;
        const headerHeight = headerElem.length ? headerElem[0].offsetHeight : 0;
        const cardHeight = cardElem.length ? cardElem[0].clientHeight : 0;
        const gapSize = 30;
        
        let cardsOffsetAmount;
        if (advisor?.cardName === 'stretched') {
          cardsOffsetAmount = data.length
        } else {
          cardsOffsetAmount = data.length > perPage.value / 2 ? 2 : 1;
        }

        // Scroll to the calculated position
        window.scrollTo({
          top: buttonPosition - cardsOffsetAmount * cardHeight - headerHeight - gapSize * (cardsOffsetAmount - 1),
          behavior: 'smooth'
        });
      }
    }
  }
}

const loadMore = async () => {
  nextPage();
  await fetchAdvisors();
};

watch(() => filters,  () => {
  resetPage();
  fetchAdvisors();
}, {deep: true});

filters.value = {
  ...defaultFilters,
  ...props.query,
};
</script>

<script lang="ts">
export default {
  name: 'SeAdvisorAdvisorsList',
};
</script>
